import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Button,
  Stack,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LinearProgress from "@mui/material/LinearProgress";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import { getCampaignActivities, getCampaignActivity } from "../../api";

const STATUS_OPTIONS = ["pending", "processing", "sent", "failed", "unknown"];

const STATUS_COLORS = {
  sent: "#008a20",
  failed: "#d30000",
  pending: "#996800",
  processing: "#2271b1",
  unknown: "#646970",
};

const cellStyle = { whiteSpace: "nowrap", padding: "6px 8px" };

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusLabel({ status }) {
  return (
    <span
      style={{
        color: STATUS_COLORS[status] || STATUS_COLORS.unknown,
        fontWeight: "bold",
        textTransform: "capitalize",
      }}
    >
      {status || "-"}
    </span>
  );
}

function EnhancedTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell sx={cellStyle}>ID</TableCell>
        <TableCell sx={cellStyle}>Customer</TableCell>
        <TableCell sx={cellStyle}>Email</TableCell>
        <TableCell sx={cellStyle}>Phone</TableCell>
        <TableCell sx={cellStyle}>Order</TableCell>
        <TableCell sx={cellStyle}>Campaign</TableCell>
        <TableCell sx={cellStyle}>Status</TableCell>
        <TableCell sx={cellStyle}>Attempts</TableCell>
        <TableCell sx={cellStyle}>Sent At</TableCell>
        <TableCell sx={cellStyle}>Email</TableCell>
        <TableCell sx={cellStyle}>Details</TableCell>
      </TableRow>
    </TableHead>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box display="flex" justifyContent="space-between" sx={{ gap: 2 }}>
      <Typography sx={{ fontSize: "13px", color: "#3c434a", fontWeight: "bold" }}>
        {label}
      </Typography>
      <Box sx={{ fontSize: "13px", textAlign: "right", wordBreak: "break-all" }}>
        {value === "" || value === null || value === undefined ? "-" : value}
      </Box>
    </Box>
  );
}

function ActivityDetailsDialog({ row, open, onClose }) {
  const rows = [
    ["Customer", row.customer_name],
    ["Email", row.email],
    ["Phone", row.phone],
    ["Order", row.order_id ? `#${row.order_id}` : "-"],
    ["Product", row.product_name],
    ["Purchase Date", formatDate(row.purchase_date)],
    ["Campaign", row.campaign_id],
    ["Status", <StatusLabel status={row.status} />],
    ["Attempts", row.attempt_count],
    ["Sent At", formatDate(row.sent_at)],
    ["Provider Message ID", row.provider_message_id],
    ["Error", row.error_message],
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Activity Details #{row.id}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        <Stack spacing={1.5}>
          {rows.map(([label, value]) => (
            <DetailRow key={label} label={label} value={value} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EmailContentDialog({ row, open, onClose }) {
  const [activity, setActivity] = useState(row);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      const res = await getCampaignActivity(row.id);
      if (res?.success && res.data) {
        setActivity(res.data);
      } else {
        setError("Unable to load the email content");
      }
      setLoading(false);
    };
    fetchActivity();
  }, [row.id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Email Sent to {row.email}</DialogTitle>
      {loading && <LinearProgress />}
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        <Typography sx={{ fontSize: "13px", color: "#3c434a", fontWeight: "bold" }}>
          Subject
        </Typography>
        <Typography sx={{ fontSize: "14px", mb: 2 }}>
          {activity.subject || "-"}
        </Typography>
        <Typography sx={{ fontSize: "13px", color: "#3c434a", fontWeight: "bold", mb: 1 }}>
          Message
        </Typography>
        {activity.html_body ? (
          <iframe
            title={`campaign-email-${row.id}`}
            srcDoc={activity.html_body}
            sandbox=""
            style={{
              width: "100%",
              height: "500px",
              border: "1px solid #dcdcde",
              backgroundColor: "#fff",
            }}
          />
        ) : (
          <Typography fontSize={13} color={error ? "error" : "inherit"}>
            {loading ? "Loading..." : error || "No email content available"}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Campaign() {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [activities, setActivities] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchvalue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(null);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError("");
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        include_html: 0,
      };
      if (search) params.email = search;
      if (status) params.status = status;

      const res = await getCampaignActivities(params);
      if (res?.success) {
        setActivities(res.data?.items || []);
        setCount(res.data?.pagination?.total || 0);
      } else {
        setActivities([]);
        setCount(0);
        setError("Unable to load campaign activity");
      }
      setLoading(false);
    };
    fetchActivities();
  }, [page, search, status, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchvalue.trim());
    setPage(0);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(0);
  };

  return (
    <Layout>
      <Box bgcolor="#f0f0f1" minHeight="calc(100vh - 67px)" px={3} py={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography sx={{ fontSize: "23px", lineHeight: 1.3 }}>
            Campaign 2026 Christmas
          </Typography>
        </Box>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={2}>
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={handleStatusChange}
            sx={{ m: 1, width: "18ch", backgroundColor: "#fff" }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option} sx={{ textTransform: "capitalize" }}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              id="campaign-search"
              sx={{ m: 1, width: "30ch", backgroundColor: "#fff", fontSize: "14px" }}
              size="small"
              placeholder="Search by email"
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchvalue}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon onClick={handleSearchSubmit} style={{ cursor: "pointer" }} />
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Stack>
        <Paper sx={{ width: "100%", mb: 2 }}>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table size="small" aria-label="campaign activity table">
              <EnhancedTableHead />
              {activities.length > 0 ? (
                <TableBody>
                  {activities.map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell sx={cellStyle}>{row.id}</TableCell>
                      <TableCell sx={cellStyle}>{row.customer_name || "-"}</TableCell>
                      <TableCell sx={cellStyle}>{row.email || "-"}</TableCell>
                      <TableCell sx={cellStyle}>{row.phone || "-"}</TableCell>
                      <TableCell sx={cellStyle}>
                        {row.order_id ? (
                          <Link
                            to={`/orders/1/details?id=${row.order_id}&tab=0`}
                            style={{ color: "#2271b1", textDecoration: "none", fontWeight: "bold" }}
                          >
                            #{row.order_id}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.campaign_id || "-"}</TableCell>
                      <TableCell sx={cellStyle}>
                        <StatusLabel status={row.status} />
                        {row.status === "failed" && row.error_message && (
                          <Typography sx={{ fontSize: "12px", color: "#646970" }}>
                            {row.error_message}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.attempt_count ?? "-"}</TableCell>
                      <TableCell sx={cellStyle}>{formatDate(row.sent_at)}</TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        <IconButton
                          size="small"
                          title="View email"
                          onClick={() => setDialog({ type: "email", row })}
                        >
                          <MailOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ padding: "6px 8px" }}>
                        <IconButton
                          size="small"
                          title="View details"
                          onClick={() => setDialog({ type: "details", row })}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={11}>
                      <Box style={{ textAlign: "center" }}>
                        <Typography color={error && !loading ? "error" : "inherit"}>
                          {loading ? "Loading..." : error || "No Email Activity Found"}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
          />
        </Paper>
        {dialog?.type === "email" && (
          <EmailContentDialog row={dialog.row} open onClose={() => setDialog(null)} />
        )}
        {dialog?.type === "details" && (
          <ActivityDetailsDialog row={dialog.row} open onClose={() => setDialog(null)} />
        )}
      </Box>
    </Layout>
  );
}
